import React from 'react'

import type {
  LotStoryGroupReadPreflightView,
  TranslateFn
} from './lotStoryLibraryTypes'

type LotStoryGroupReadPreflightCardProps = {
  t: TranslateFn
  preflight: LotStoryGroupReadPreflightView
  testIdPrefix: string
  onSelectGroup?: (groupId: string) => void
}

const getTestId = (prefix: string, suffix?: string) => suffix ? `${prefix}-${suffix}` : prefix

export default function LotStoryGroupReadPreflightCard({
  t,
  preflight,
  testIdPrefix,
  onSelectGroup
}: Readonly<LotStoryGroupReadPreflightCardProps>) {
  const isLoading = preflight.status === 'loading' || preflight.status === 'idle'
  const statusMessage = preflight.status === 'allowed'
    ? t('lots.story.groupPreflight.status.allowed')
    : t('lots.story.groupPreflight.status.denied')

  return (
    <div
      data-testid={getTestId(testIdPrefix)}
      style={{
        border: '1px solid #bfdbfe',
        borderRadius: 10,
        backgroundColor: '#eff6ff',
        padding: 12,
        display: 'grid',
        gap: 8
      }}
    >
      <div style={{ display: 'grid', gap: 4 }}>
        <strong style={{ fontSize: 12, color: '#1d4ed8' }}>{t('lots.story.groupPreflight.title')}</strong>
        <div style={{ fontSize: 13, color: '#1d4ed8' }}>{t('lots.story.groupPreflight.subtitle')}</div>
      </div>

      {isLoading ? (
        <div data-testid={getTestId(testIdPrefix, 'status')} style={{ fontSize: 13, color: '#1d4ed8', fontWeight: 700 }}>
          {t('lots.story.groupPreflight.loading')}
        </div>
      ) : preflight.errorMessage ? (
        <div data-testid={getTestId(testIdPrefix, 'status')} style={{ fontSize: 13, color: '#b91c1c', fontWeight: 700 }}>
          {preflight.errorMessage}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 6, fontSize: 13, color: '#1e3a8a' }}>
          {preflight.groupOptions.length > 0 ? (
            <label style={{ display: 'grid', gap: 4 }}>
              <strong>{t('lots.story.groupPreflight.targetGroup')}</strong>
              <select
                value={preflight.selectedGroupId}
                onChange={(event) => onSelectGroup?.(event.target.value)}
                data-testid={getTestId(testIdPrefix, 'group-select')}
                style={{ borderRadius: 8, border: '1px solid #93c5fd', padding: '8px 10px', color: '#1e3a8a', backgroundColor: '#ffffff' }}
              >
                {preflight.groupOptions.map((option) => (
                  <option key={option.groupId} value={option.groupId}>{option.label}</option>
                ))}
              </select>
            </label>
          ) : null}
          <div data-testid={getTestId(testIdPrefix, 'selected-group')}>
            <strong>{t('lots.story.groupPreflight.targetGroup')}:</strong> {preflight.selectedGroupLabel || t('lots.story.groupPreflight.noEligibleGroups')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'identity')}>
            <strong>{t('lots.story.groupPreflight.identity')}:</strong> {preflight.identityLabel || t('lots.story.groupPreflight.none')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'role')}>
            <strong>{t('lots.story.groupPreflight.tenantRole')}:</strong> {preflight.roleLabel || t('lots.story.groupPreflight.none')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'visibility')}>
            <strong>{t('lots.story.groupPreflight.visibility')}:</strong> {preflight.artifactVisibilityLabel || t('lots.story.groupPreflight.none')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'groups')}>
            <strong>{t('lots.story.groupPreflight.eligibleGroups')}:</strong> {preflight.eligibleGroupCount}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'partners')}>
            <strong>{t('lots.story.groupPreflight.partnerOrganizations')}:</strong> {preflight.partnerOrganizationsLabel || t('lots.story.groupPreflight.none')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'reasons')}>
            <strong>{t('lots.story.groupPreflight.blockingReasons')}:</strong> {preflight.blockingReasonLabels.length > 0
              ? preflight.blockingReasonLabels.join('; ')
              : t('lots.story.groupPreflight.none')}
          </div>
          <div
            data-testid={getTestId(testIdPrefix, 'status')}
            style={{
              color: preflight.status === 'allowed' ? '#166534' : '#b91c1c',
              fontWeight: 700
            }}
          >
            {statusMessage}
          </div>
        </div>
      )}
    </div>
  )
}